import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Heart, ArrowLeft, ArrowRight, CheckCircle2, 
  Building2, User, Globe, Phone, Mail, MapPin, Loader2,
  FileText, AlertCircle
} from 'lucide-react';
import Logo from '../Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CharityApplication() {
  const [step, setStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  
  const [formData, setFormData] = useState({
    organization_name: '',
    ein_tax_id: '',
    category: '',
    description: '',
    mission_statement: '',
    website: '',
    logo_url: '',
    primary_contact_name: '',
    primary_contact_email: '',
    primary_contact_phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/charity-portal/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.organization_name.trim()) newErrors.organization_name = 'Required';
    if (!formData.ein_tax_id.trim()) newErrors.ein_tax_id = 'Required';
    if (!formData.category) newErrors.category = 'Required';
    if (!formData.description.trim()) newErrors.description = 'Required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.primary_contact_name.trim()) newErrors.primary_contact_name = 'Required';
    if (!formData.primary_contact_email.trim()) newErrors.primary_contact_email = 'Required';
    if (!formData.primary_contact_phone.trim()) newErrors.primary_contact_phone = 'Required';
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.primary_contact_email && !emailRegex.test(formData.primary_contact_email)) {
      newErrors.primary_contact_email = 'Invalid email format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/charity-portal/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setApplicationId(data.application_id);
        setSubmitted(true);
        toast.success('Application submitted successfully!');
      } else {
        toast.error(data.detail || 'Failed to submit application');
      }
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg shadow-2xl border-0">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h2>
            <p className="text-gray-600 mb-6">
              Thank you for applying to join DealShaq as a charity partner.
              We'll review your application within 3-5 business days.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-500">Application Reference</p>
              <p className="font-mono text-lg font-semibold text-gray-900">{applicationId?.slice(0, 8)}</p>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              A confirmation email has been sent to <strong>{formData.primary_contact_email}</strong>
            </p>
            <Link to="/charity">
              <Button variant="outline" className="w-full">
                Back to DealShaq Charity App
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Logo size="large" />
          <div className="flex items-center justify-center gap-2 mt-4 mb-2">
            <Heart className="w-6 h-6 text-emerald-600 fill-emerald-600" />
            <h1 className="text-2xl font-bold text-gray-900">Charity Partner Application</h1>
          </div>
          <p className="text-gray-600">Join DealShaq and receive donations from participating retailers</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${
                step >= s ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {step > s ? <CheckCircle2 className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-16 h-1 mx-1 ${step > s ? 'bg-emerald-600' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between text-xs text-gray-500 px-4 mb-8">
          <span>Organization</span>
          <span>Contact Info</span>
          <span>Review</span>
        </div>

        <Card className="shadow-xl border-0">
          {/* Step 1: Organization Info */}
          {step === 1 && (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-emerald-600" />
                  Organization Information
                </CardTitle>
                <CardDescription>
                  Tell us about your nonprofit organization
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="org_name">Organization Name *</Label>
                  <Input
                    id="org_name"
                    value={formData.organization_name}
                    onChange={(e) => updateField('organization_name', e.target.value)}
                    placeholder="e.g., Cornerstones VA"
                    className={errors.organization_name ? 'border-red-500' : ''}
                  />
                  {errors.organization_name && (
                    <p className="text-xs text-red-500">{errors.organization_name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ein">EIN / Tax ID *</Label>
                  <Input
                    id="ein"
                    value={formData.ein_tax_id}
                    onChange={(e) => updateField('ein_tax_id', e.target.value)}
                    placeholder="XX-XXXXXXX"
                    className={errors.ein_tax_id ? 'border-red-500' : ''}
                  />
                  {errors.ein_tax_id && (
                    <p className="text-xs text-red-500">{errors.ein_tax_id}</p>
                  )}
                  <p className="text-xs text-gray-500">Your 501(c)(3) tax identification number</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => updateField('category', value)}
                  >
                    <SelectTrigger className={errors.category ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.category && (
                    <p className="text-xs text-red-500">{errors.category}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Brief Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    placeholder="What does your organization do?"
                    rows={3}
                    className={errors.description ? 'border-red-500' : ''}
                  />
                  {errors.description && (
                    <p className="text-xs text-red-500">{errors.description}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mission">Mission Statement (Optional)</Label>
                  <Textarea
                    id="mission"
                    value={formData.mission_statement}
                    onChange={(e) => updateField('mission_statement', e.target.value)}
                    placeholder="Your organization's mission"
                    rows={2}
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="website">Website (Optional)</Label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="website"
                        type="url"
                        className="pl-10"
                        value={formData.website}
                        onChange={(e) => updateField('website', e.target.value)}
                        placeholder="https://"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="logo">Logo URL (Optional)</Label>
                    <Input
                      id="logo"
                      type="url"
                      value={formData.logo_url}
                      onChange={(e) => updateField('logo_url', e.target.value)}
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 2: Contact Info */}
          {step === 2 && (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-emerald-600" />
                  Contact Information
                </CardTitle>
                <CardDescription>
                  Primary contact for donation inquiries and portal access
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_name">Contact Name *</Label>
                  <Input
                    id="contact_name"
                    value={formData.primary_contact_name}
                    onChange={(e) => updateField('primary_contact_name', e.target.value)}
                    placeholder="Jane Smith"
                    className={errors.primary_contact_name ? 'border-red-500' : ''}
                  />
                  {errors.primary_contact_name && (
                    <p className="text-xs text-red-500">{errors.primary_contact_name}</p>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact_email">Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="contact_email"
                        type="email"
                        className="pl-10"
                        value={formData.primary_contact_email}
                        onChange={(e) => updateField('primary_contact_email', e.target.value)}
                        placeholder="contact@charity.org"
                      />
                    </div>
                    {errors.primary_contact_email && (
                      <p className="text-xs text-red-500">{errors.primary_contact_email}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_phone">Phone *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="contact_phone"
                        type="tel"
                        className="pl-10"
                        value={formData.primary_contact_phone}
                        onChange={(e) => updateField('primary_contact_phone', e.target.value)}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    {errors.primary_contact_phone && (
                      <p className="text-xs text-red-500">{errors.primary_contact_phone}</p>
                    )}
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <Label className="flex items-center gap-2 mb-3">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    Organization Address (Optional)
                  </Label>
                  <div className="space-y-3">
                    <Input
                      value={formData.address}
                      onChange={(e) => updateField('address', e.target.value)}
                      placeholder="Street Address"
                    />
                    <div className="grid grid-cols-6 gap-3">
                      <Input
                        className="col-span-3"
                        value={formData.city}
                        onChange={(e) => updateField('city', e.target.value)}
                        placeholder="City"
                      />
                      <Input
                        className="col-span-1"
                        value={formData.state}
                        onChange={(e) => updateField('state', e.target.value)}
                        placeholder="State"
                        maxLength={2}
                      />
                      <Input
                        className="col-span-2"
                        value={formData.zip_code}
                        onChange={(e) => updateField('zip_code', e.target.value)}
                        placeholder="ZIP Code"
                        maxLength={10}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  Review Your Application
                </CardTitle>
                <CardDescription>
                  Please verify your information before submitting
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Building2 className="w-4 h-4" /> Organization
                    </h4>
                    <div className="mt-2 grid sm:grid-cols-2 gap-2 text-sm">
                      <div><span className="text-gray-500">Name:</span> {formData.organization_name}</div>
                      <div><span className="text-gray-500">EIN:</span> {formData.ein_tax_id}</div>
                      <div><span className="text-gray-500">Category:</span> {formData.category}</div>
                      {formData.website && (
                        <div><span className="text-gray-500">Website:</span> {formData.website}</div>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-2">{formData.description}</p>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                      <User className="w-4 h-4" /> Primary Contact
                    </h4>
                    <div className="mt-2 grid sm:grid-cols-2 gap-2 text-sm">
                      <div><span className="text-gray-500">Name:</span> {formData.primary_contact_name}</div>
                      <div><span className="text-gray-500">Email:</span> {formData.primary_contact_email}</div>
                      <div><span className="text-gray-500">Phone:</span> {formData.primary_contact_phone}</div>
                    </div>
                  </div>

                  {(formData.address || formData.city) && (
                    <div className="border-t pt-4">
                      <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                        <MapPin className="w-4 h-4" /> Address
                      </h4>
                      <p className="text-sm text-gray-600 mt-2">
                        {formData.address}{formData.address && ', '}
                        {formData.city}{formData.city && ', '}{formData.state} {formData.zip_code}
                      </p>
                    </div>
                  )}
                </div>

                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">What happens next?</p>
                    <ul className="mt-2 space-y-1 text-blue-700">
                      <li>• We'll review your application within 3-5 business days</li>
                      <li>• Once approved, you'll receive an email with portal access</li>
                      <li>• You can then set up payments to start receiving donations</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </>
          )}

          {/* Navigation */}
          <div className="p-6 pt-0 flex justify-between">
            {step > 1 ? (
              <Button variant="outline" onClick={() => setStep(step - 1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            ) : (
              <Link to="/charity">
                <Button variant="outline">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </Link>
            )}

            {step < 3 ? (
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleNext}>
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700" 
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Application
                    <CheckCircle2 className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Already a partner? <Link to="/charity" className="text-emerald-600 hover:underline">Login to your portal</Link>
        </p>
      </div>
    </div>
  );
}
